import { useCallback } from 'react';
import { BRAND, WHATSAPP_MESSAGES } from '@/lib/constants/futurestation';

export type WhatsAppContext = 'hero' | 'services' | 'contact' | 'flows' | 'outsourcing' | 'default';

interface UseWhatsAppReturn {
  openWhatsApp: (context?: WhatsAppContext, customMessage?: string) => void;
  formatWhatsAppURL: (message: string) => string;
  trackWhatsAppClick: (context: WhatsAppContext) => void;
}

export const useWhatsApp = (): UseWhatsAppReturn => {
  const formatWhatsAppURL = useCallback((message: string): string => {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${BRAND.phoneNumber}?text=${encodedMessage}`;
  }, []);

  const trackWhatsAppClick = useCallback((context: WhatsAppContext) => {
    // Analytics tracking
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'whatsapp_click', {
        event_category: 'engagement',
        event_label: context,
        custom_parameter_1: context
      });
    }
    
    // Console log for development
    console.log(`WhatsApp clicked - Context: ${context}`);
  }, []);

  const openWhatsApp = useCallback((
    context: WhatsAppContext = 'default', 
    customMessage?: string
  ) => {
    const message = customMessage || WHATSAPP_MESSAGES[context];
    const url = formatWhatsAppURL(message);
    
    // Track the click
    trackWhatsAppClick(context);
    
    // Open WhatsApp
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [formatWhatsAppURL, trackWhatsAppClick]);

  return {
    openWhatsApp,
    formatWhatsAppURL,
    trackWhatsAppClick
  };
};

// Working hours helper (optional)
export const useWorkingHours = () => {
  const isWorkingHours = useCallback((): boolean => {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay(); // 0 = Sunday, 6 = Saturday
    
    // Monday to Friday, 8 AM to 6 PM (Brazilian time)
    const isWeekday = day >= 1 && day <= 5;
    const isBusinessHour = hour >= 8 && hour < 18;
    
    return isWeekday && isBusinessHour;
  }, []);

  const getWorkingHoursMessage = useCallback((): string => {
    if (isWorkingHours()) {
      return "Estamos online agora!";
    }
    return "Deixe sua mensagem que responderemos em breve! Horário: Seg-Sex, 8h às 18h";
  }, [isWorkingHours]);

  return {
    isWorkingHours,
    getWorkingHoursMessage
  };
};

// Global type for gtag (Google Analytics)
declare global {
  interface Window {
    gtag?: (
      command: string,
      action: string,
      options?: Record<string, string | number | boolean>
    ) => void;
  }
}